import React, { PropsWithChildren, useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare } from '@fortawesome/free-solid-svg-icons';
import { faSquare } from '@fortawesome/free-regular-svg-icons';
import 'bulma/css/bulma.min.css';

import { truthyJoin } from '../utils.tsx';
import './sortableTable.css';
import './sortableHeaders.css';


type Coordinates = {x: number, y: number};


// These are the functions needed by the sortable table.
interface SortableTableFunctions {
  renderHeaderCell?: (x: number) => React.JSX.Element;
  renderTableCell?: (coordinates: Coordinates) => React.JSX.Element;
  // Unlike the EditableTable, the SortableTable returns row identifiers, rather than row numbers.
  rowSelectionChanged?: (selectedRows: string[]) => void;
}


interface SortableTableProps {
  extraClasses?: string;
  rowIdentifiers: string[];
  columnCount: number;
  hiddenColumns?: number[];
  displayRowSelectionBoxes: boolean;
  tableFunctions: SortableTableFunctions;
}


// Given a DOM node, walks up the tree looking for a node of type "td"
// with "cellX" and "cellY" values in its dataset.
// If those values are present, return them, otherwise return null.
function findACell(node: HTMLElement): Coordinates | null {
  var n = node.nodeName || "";
  n = n.trim().toLowerCase();
  if (n == "td") {
    const x = node.dataset.cellX;
    const y = node.dataset.cellY;
    if ((x !== undefined) && (y !== undefined)) {
      return { x: parseInt(x, 10), y: parseInt(y, 10)};
    }
  }
  const p = node.parentNode;
  if (!p) { return null; } else { return findACell(p as HTMLElement); }
}


const SortableTable: React.FC<PropsWithChildren<SortableTableProps>> = (props) => {

  const [cellSelections, setCellSelections] = useState<Set<string>>(new Set());
  const tableRef = useRef<HTMLTableElement>(null);

  const [cellMouseDownOverRowId, setCellMouseDownOverRowId] = useState<string | null>(null);
  const [mouseDownOnChecked, setMouseDownOnChecked] = useState<boolean>(false);

  const hiddenColumnSet = new Set(props.hiddenColumns ?? []);

  useEffect(() => {
    if (props.displayRowSelectionBoxes && props.tableFunctions.rowSelectionChanged) {
      if (cellMouseDownOverRowId !== null) {
        if (mouseDownOnChecked) {
          cellSelections.delete(cellMouseDownOverRowId);
        } else {
          cellSelections.add(cellMouseDownOverRowId);
        }
      }
      // Filter cellSelections based on rowIdentifiers, in case the list has rows removed from it
      const newCellSelections = new Set<string>();
      props.rowIdentifiers.forEach((id) => {
        if (cellSelections.has(id)) {
          newCellSelections.add(id);
        }
      });
      setCellSelections(newCellSelections);
      props.tableFunctions.rowSelectionChanged(Array.from(newCellSelections));
    }
  }, [cellMouseDownOverRowId, props.rowIdentifiers]);


  function tableOnPointerDown(event: React.PointerEvent<HTMLElement>) {
    const foundCell = findACell(event.target as HTMLElement);
    if (foundCell) {
      setCellMouseDownOverRowId(props.rowIdentifiers[foundCell.y]);
      setMouseDownOnChecked(cellSelections.has(props.rowIdentifiers[foundCell.y]));

      // We can't do this because it creates a reference to the function in a previous
      // instance of the SampleTable react object, which contains the old useState values,
      // sowing chaos and confusion.
      // document.addEventListener("mouseup", tableOnMouseUp, {once: true});

      // We can't do this either because it redirects all subsequent events so their
      // target is the table itself, obscuring the element that the pointer was originally over,
      // making the event useless to us.
      //const foundTable = findEnclosingTable(event.target as HTMLElement);
      //if (foundTable) {
      //  foundTable.setPointerCapture(event.pointerId);
      //}
    } else {
      setCellMouseDownOverRowId(null);
      setMouseDownOnChecked(false);
    }
  }


  function tableOnPointerOver(event: React.PointerEvent<HTMLElement>) {
    // We do no tracking if the mouse isn't down
    if (cellMouseDownOverRowId === null) { return; }
    const foundCell = findACell(event.target as HTMLElement);
    if (!foundCell) {
      return;
    }
    const identifier = props.rowIdentifiers[foundCell.y];
    if (identifier !== cellMouseDownOverRowId) {
      setCellMouseDownOverRowId(identifier);
    }
  }


  function tableOnPointerUp(event: React.PointerEvent<HTMLElement>) {
    setCellMouseDownOverRowId(null);
  }


  var headers: React.JSX.Element[] = [];
  if (props.tableFunctions.renderHeaderCell) {
    if (props.displayRowSelectionBoxes && props.tableFunctions.rowSelectionChanged) {
      headers.push(
        <th key="_selection" className="rowselectionheader"></th>
      );
    }
    for (var x = 0; x < props.columnCount; x++) {
      if (!hiddenColumnSet.has(x)) {
        headers.push(props.tableFunctions.renderHeaderCell(x));
      }
    }
  }

  var rows: React.JSX.Element[] = [];
  const renderTableCell = props.tableFunctions.renderTableCell;
  if (typeof renderTableCell === "function") {

    props.rowIdentifiers.forEach((rowId, y) => {
      const isSelected = cellSelections.has(rowId);
      var cells: React.JSX.Element[] = [];
      if (props.displayRowSelectionBoxes && props.tableFunctions.rowSelectionChanged) {
        cells.push(
          <td key={`selection-${y}`} className={truthyJoin("rowselectioncell", isSelected && "selected")}>
            <span className="icon">
              <FontAwesomeIcon icon={isSelected ? faCheckSquare : faSquare} />
            </span>
          </td>
        );
      }
      for (var x = 0; x < props.columnCount; x++) {
        if (hiddenColumnSet.has(x)) {
          continue;
        }

        const cellContent = renderTableCell({x: x, y: y});
        const cellClasses = truthyJoin(
                "sortablecell",
                isSelected && "inSelection"
              );
        cells.push((
          <td key={ `cell-${x}-${y}` }
            data-cell-x={ x }
            data-cell-y={ y }
            className={ cellClasses }
          >
            { cellContent }
          </td>
        ));
      }
      rows.push( <tr key={ `row-${rowId}` }>{ cells }</tr> );
    });
  }

  return (
    <table className={truthyJoin("sortabletable sortableheaders striped", props.extraClasses)}
            tabIndex={0}
            ref={ tableRef }
            onPointerOver={ tableOnPointerOver }
            onPointerDown={ tableOnPointerDown }
            onPointerUp={ tableOnPointerUp }
        >
      <thead>
        <tr key="headers">
          { headers }
        </tr>
      </thead>
      <tbody>
        { rows }
      </tbody>
    </table>
  );
}

export { SortableTable };
export type { Coordinates, SortableTableFunctions };